import React, { FC, useState, useEffect } from 'react';
import { Modal, Spin } from 'antd';
import { CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import '../mediaPreviewModal.scss';

export interface FullScreenDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentUrl: string;
  type?: 'pdf' | 'image' | 'doc' | 'docx' | 'auto' | string;
  title?: string;
}

export const FullScreenDocumentModal: FC<FullScreenDocumentModalProps> = ({
  isOpen,
  onClose,
  documentUrl,
  type = 'auto',
  title = 'Document Preview',
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadedUrl, setLoadedUrl] = useState('');

  const isDocument =
    type === 'pdf' ||
    type === 'doc' ||
    type === 'docx' ||
    (type === 'auto' &&
      (documentUrl.toLowerCase().includes('.pdf') ||
        documentUrl.toLowerCase().includes('.doc') ||
        documentUrl.toLowerCase().includes('application/pdf')));

  useEffect(() => {
    if (isOpen && documentUrl !== loadedUrl) {
      setIsLoading(true);
    }
  }, [isOpen, documentUrl, loadedUrl]);

  const antIcon = <LoadingOutlined style={{ fontSize: 48, color: '#ffa100' }} spin />;

  return (
    <Modal
      className="media-preview-modal"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      width="80vw"
      style={{ maxWidth: '90vw' }}
      destroyOnHidden
      closable={true}
      closeIcon={
        <span
          style={{
            color: '#fff',
            fontSize: '15px',
            backgroundColor: 'rgba(15, 23, 42, 0.9)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
          }}
        >
          <CloseOutlined />
        </span>
      }
      styles={{
        mask: {
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(20, 22, 28, 0.72)',
        },
        body: {
          padding: 0,
          background: '#1a1f2e',
          borderRadius: '10px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        },
      }}
    >
      {documentUrl && (
        <div
          style={{
            width: '100%',
            minHeight: '75vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            borderRadius: '10px',
            overflow: 'hidden',
            background: '#1a1f2e',
          }}
        >
          {isLoading && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2,
                background: '#1a1f2e',
              }}
            >
              <Spin indicator={antIcon} />
            </div>
          )}

          {isDocument ? (
            <iframe
              title={title}
              src={documentUrl}
              onLoad={() => {
                setIsLoading(false);
                setLoadedUrl(documentUrl);
              }}
              style={{
                width: '100%',
                height: '75vh',
                border: 'none',
                borderRadius: '10px',
                display: 'block',
                background: '#fff',
                opacity: isLoading ? 0 : 1,
                transition: 'opacity 0.2s ease',
              }}
            />
          ) : (
            <img
              src={documentUrl}
              alt={title}
              onLoad={() => {
                setIsLoading(false);
                setLoadedUrl(documentUrl);
              }}
              style={{
                width: '100%',
                maxHeight: '75vh',
                objectFit: 'contain',
                borderRadius: '10px',
                display: isLoading ? 'none' : 'block',
                margin: '0 auto',
              }}
            />
          )}
        </div>
      )}
    </Modal>
  );
};
